// Campus "places" — rooms, areas and fixtures with extra info on top of the map
// data: study spots, libraries, restaurants, bathrooms, clubs, offices, and
// amenities (a microwave, a vending machine, a water station).
//
// The pathfinder surfaces the ones people go looking for mid-day — study spots,
// libraries, bathrooms and microwaves — through the "More Places" panel. The
// rest are carried through so a host can widen `SHOWN_KINDS` without touching
// this file.
//
// A place knows *where* it is in one of two ways, and both need resolving to
// something the map can draw:
//
//   * a drawn area (`polygons`, lat/lng rings) — a hallway study nook, say;
//   * a room number in a building — which is most of them, and which has to be
//     matched against that building's structure polygons on the right floor.
//
// `resolvePlace` does both and hands back the same `{ position, floor, rings }`
// shape `getRoomLocation` produces, so the map layer treats them alike.

import { haversineMeters } from './geo.js';

/** Kinds the "More Places" panel lists, in tab order. */
export const SHOWN_KINDS = ['study', 'library', 'bathroom', 'busstop', 'amenity:microwave'];

/**
 * What a place counts AS here — its type, except for **amenities**, which split
 * by category: `amenity:microwave`, `amenity:vending`, and so on.
 *
 * The authoring tool stores every one of these as `type: 'amenity'` with a
 * category on the side, because to an author they are one kind of thing to
 * place. To someone standing on campus they are not: "where can I heat my
 * lunch" and "where can I buy a drink" are different questions, and one merged
 * Amenities tab would answer neither. So the category is the kind here.
 */
export const placeKindKey = (p) =>
  (p?.type === 'amenity' && p.amenity ? `amenity:${p.amenity}` : p?.type);

// `icon` names a glyph in components/PlaceIcons.jsx — drawn, not emoji, so the
// panel matches the map pins and renders identically on every platform.
//
// That is also why amenity categories are listed here one at a time rather than
// read off the data: the authoring tool lets an author invent a category with an
// emoji, and an emoji is the one thing this set will not render. A category with
// no entry below simply never reaches the panel (`attachPlaces` keeps only
// `SHOWN_KINDS`), which is a quiet absence rather than a broken row — but it does
// mean shipping a new category is three edits: a line here, a line in
// SHOWN_KINDS, and a glyph in PlaceIcons.jsx + useRouteLayer.js.
export const PLACE_KINDS = {
  study:      { label: 'Study Spot', plural: 'Study spots', icon: 'study',    color: '#3949ab' },
  library:    { label: 'Library',    plural: 'Libraries',   icon: 'library',  color: '#6d4c41' },
  restaurant: { label: 'Restaurant', plural: 'Restaurants', icon: 'seat',     color: '#e64a19' },
  bathroom:   { label: 'Bathroom',   plural: 'Bathrooms',   icon: 'bathroom', color: '#00838f' },
  club:       { label: 'Club',       plural: 'Clubs',       icon: 'group',    color: '#8e24aa' },
  office:     { label: 'Office',     plural: 'Offices',     icon: 'pin',      color: '#455a64' },
  // Not authored in places.json like the rest: bus stops come from Winnipeg
  // Transit (transit_stops.json) and are turned into place-shaped entries by
  // transitStopPlaces(). They are listed here because to somebody standing on
  // campus a bus stop is exactly the same kind of question as a washroom —
  // "where is the nearest one, and is it any use to me right now".
  // Dark blue, matching the filled pin the map draws for a stop (busPinIcon in
  // useRouteLayer.js) — the tab, the row thumbs and the pin are one thing.
  busstop: { label: 'Bus Stop', plural: 'Bus stops', icon: 'bus', color: '#0d3b66' },
  'amenity:microwave': {
    label: 'Microwave', plural: 'Microwaves', icon: 'microwave', color: '#2e7d32',
  },
};
export const kindOf = (p) =>
  PLACE_KINDS[placeKindKey(p)] || PLACE_KINDS[p?.type] || PLACE_KINDS.study;

// The capacity/plugs/zone/tables filters describe study spots and nothing else:
// all 5 libraries carry no capacity and no amenities, and bathrooms have their
// own vocabulary. Showing the row elsewhere would filter every row away.
export const FILTERABLE_KINDS = new Set(['study', 'bathroom']);

/** A place's colour: a bathroom takes its type's colour, everything else its kind's. */
export function placeColor(place) {
  if (place?.type === 'bathroom') return BATHROOM_COLORS[place.bathroomType] || PLACE_KINDS.bathroom.color;
  return kindOf(place).color;
}

/** A place's glyph name — bathrooms differ by type. See components/PlaceIcons.jsx. */
export function placeIconName(place) {
  if (place?.type === 'bathroom') return `bathroom-${place.bathroomType || 'universal'}`;
  return kindOf(place).icon;
}

/** Amenity keys → chip label + glyph. Mirrors STUDY_AMENITIES in the tool. */
export const AMENITY_LABELS = {
  plugs:    { text: 'Plugs',        icon: 'plug' },
  cushions: { text: 'Cushions',     icon: 'cushion' },
  quiet:    { text: 'Quiet zone',   icon: 'quiet' },
  loud:     { text: 'Loud zone',    icon: 'loud' },
  noTables: { text: 'Chairs only',  icon: 'chair' },
};

// Seat counts, matching CAPACITY_LEVELS in the authoring tool. A side-on seat
// rather than the face-on chair used for "Chairs only", so the two don't read
// as the same claim.
const CAPACITY_LABELS = {
  low:    { text: 'Few Seats',    icon: 'seat' },
  medium: { text: 'Medium Seats', icon: 'seat' },
  high:   { text: 'Many Seats',   icon: 'seat' },
};

// Bathroom vocabulary + brand colours, matching Welcome Day's bathroom finder
// (men's blue, women's light red, universal purple) so the two features don't
// disagree about what a washroom looks like.
export const BATHROOM_LABELS = { mens: "Men's", womens: "Women's", universal: 'Universal' };
export const BATHROOM_COLORS = { mens: '#0060a8', womens: '#e5556a', universal: '#8e24aa' };

/** Chips for a place — `{ key, text, icon }` — what it is, then what it has. */
export function placeChips(place) {
  const chips = [];
  if (place.type === 'bathroom') {
    const label = BATHROOM_LABELS[place.bathroomType];
    if (label) {
      chips.push({
        key: 'bathroomType', text: label,
        icon: placeIconName(place), color: placeColor(place),
      });
    }
    if (place.accessible) chips.push({ key: 'accessible', text: 'Accessible', icon: 'accessible' });
    if (place.periodSupply) chips.push({ key: 'period', text: 'Period products', icon: 'period' });
    if (place.hours) chips.push({ key: 'hours', text: place.hours, icon: 'clock' });
    return chips;
  }
  const cap = CAPACITY_LABELS[place.capacity];
  if (cap) chips.push({ key: 'capacity', ...cap });
  if (place.hours) chips.push({ key: 'hours', text: place.hours, icon: 'clock' });
  if (place.groupStudyRooms) chips.push({ key: 'group', text: 'Group rooms', icon: 'group' });
  for (const [key, on] of Object.entries(place.amenities || {})) {
    if (on && AMENITY_LABELS[key]) chips.push({ key, ...AMENITY_LABELS[key] });
  }
  return chips;
}

/**
 * The author's note about where a thing actually is — "Near Foodcourt", "Arts
 * Student Body Council Lounge".
 *
 * It matters most for the places that have no room number to give: a microwave
 * pinned to a corridor node has a building and a floor and nothing else, so
 * without this its subtitle is a bare building name and the reader is left
 * hunting the floor for it.
 */
export const placeNote = (p) => String(p?.notes || '').trim();

export function placeTitle(place) {
  const name = String(place?.name || '').trim();
  if (name) return name;
  const room = String(place?.roomNumber || '').trim();
  return room ? `${kindOf(place).label} ${room}` : kindOf(place).label;
}

/** Mean of a set of lat/lng rings. */
function ringsCentroid(rings) {
  let lat = 0, lng = 0, n = 0;
  for (const ring of rings) for (const pt of ring) { lat += pt.lat; lng += pt.lng; n += 1; }
  return n ? { lat: lat / n, lng: lng / n } : null;
}

/** A place's own drawn areas as lat/lng rings, tolerating both key spellings. */
function drawnRings(place) {
  return (place?.polygons || [])
    .map(poly => (poly || [])
      .map(pt => ({
        lat: pt.lat ?? pt.latitude,
        lng: pt.lng ?? pt.longitude,
      }))
      .filter(pt => Number.isFinite(pt.lat) && Number.isFinite(pt.lng)))
    .filter(ring => ring.length >= 3);
}

/**
 * Where a place sits on the map: `{ position, floor, rings, building }`, the
 * same shape `getRoomLocation` returns — or null when it cannot be placed.
 *
 * Preference order matters. A drawn area is what the author actually traced, so
 * it wins; a room number is resolved against the building's polygons, which
 * also *discovers* the floor for the many places that never recorded one (the
 * imported room lists know only a building and a room). A bare lat/lng is the
 * last resort and needs a floor, since a point alone can't say which one.
 */
export function resolvePlace(mapData, place) {
  if (!place) return null;
  const building = place.building || null;
  const b = building ? mapData.buildings.find(x => x.slug === building) : null;

  const drawn = drawnRings(place);
  if (drawn.length) {
    return {
      position: ringsCentroid(drawn),
      floor: place.floor ?? 1,
      rings: drawn,
      building,
    };
  }

  const room = String(place.roomNumber || '').trim().toLowerCase();
  if (b && room) {
    for (const [floor, polys] of b.polygonsByFloor) {
      if (place.floor != null && floor !== place.floor) continue;
      const rings = polys
        .filter(p => String(p.roomNumber).toLowerCase() === room)
        .map(p => p.ring);
      if (rings.length) {
        return { position: ringsCentroid(rings), floor, rings, building };
      }
    }
  }

  // An attached nav node (set in the Places editor's "Attach node") is the
  // author saying exactly where this place is reached. It sits below the two
  // polygon tiers because a shape highlights far better than a point does — but
  // above the room-number guesses, and it settles the floor outright.
  if (place.navNodeId) {
    const node = mapData.nodes.get(place.navNodeId);
    if (node) {
      return {
        position: { lat: node.lat, lng: node.lng },
        floor: node.floor ?? place.floor ?? 1,
        rings: [],
        building,
      };
    }
  }

  // No polygon carries that room number — very common for bathroom sub-rooms
  // (218B, 101C, SP118…) which are drawn as part of a larger space. The nav
  // graph still knows the door, which gives both a position and the floor.
  // This is the same fallback Welcome Day's bathroom finder makes.
  if (room) {
    const entry = mapData.roomIndex.find(
      e => e.building === building && String(e.roomNumber).toLowerCase() === room
    );
    const node = entry && mapData.nodes.get(entry.nodeId);
    if (node) {
      return {
        position: { lat: node.lat, lng: node.lng },
        floor: node.floor ?? place.floor ?? 1,
        rings: [],
        building,
      };
    }
  }

  if (Number.isFinite(place.lat) && Number.isFinite(place.lng)) {
    return {
      position: { lat: place.lat, lng: place.lng },
      floor: place.floor ?? 1,
      rings: [],
      building,
    };
  }
  return null;
}

/**
 * Fold host-supplied extra photos — `{ [placeId]: [url, …] }` — onto the places
 * they belong to, after each place's own authored images.
 *
 * This is how a student's accepted submission reaches the panel: the places
 * file is a static snapshot that only the authoring tool rewrites, so approved
 * photos ride alongside it as an overlay rather than being merged into it.
 * Unknown ids and duplicates are ignored, so a stale overlay is harmless.
 */
export function mergePlacePhotos(places, extraByPlaceId) {
  if (!extraByPlaceId || !places?.length) return places;
  return places.map(place => {
    const extra = extraByPlaceId[place?.id];
    if (!Array.isArray(extra) || !extra.length) return place;
    const own = place.images || [];
    const added = extra.filter(url => url && !own.includes(url));
    return added.length ? { ...place, images: [...own, ...added] } : place;
  });
}

/**
 * Attach the places a host supplied to `mapData`, resolved and ready to list.
 *
 * Each entry is `{ place, loc, buildingName }`. Places that cannot be put on
 * the map at all are dropped — the panel's whole job is "take me there", and a
 * row that cannot answer that is worse than no row.
 *
 * Returns the array, and also stashes it on `mapData.places`.
 */
export function attachPlaces(mapData, places, { kinds = SHOWN_KINDS } = {}) {
  const wanted = new Set(kinds);
  const nameBySlug = new Map(mapData.buildings.map(b => [b.slug, b.name]));

  // Which rooms are washrooms, keyed the way `roomIndex` keys rooms, so the map
  // can tint their polygons the way it tints stairs and elevators. "Where is the
  // nearest toilet" is asked of a campus map more than anything except "where is
  // this room", and answering it should not require opening a panel.
  //
  // Built from the RAW list rather than from the resolved places below, and
  // independently of `kinds`: a host that narrows the Nearby tabs is saying what
  // to LIST, not that washrooms stopped being washrooms. 42 bathrooms also fail
  // to resolve (sub-rooms like 218B that appear in neither the polygons nor the
  // nav graph) — those simply have no polygon to tint, which costs nothing.
  const bathroomRooms = new Set();
  for (const place of places || []) {
    if (place?.type !== 'bathroom' || !place.building || !place.roomNumber) continue;
    bathroomRooms.add(`${place.building}::${String(place.roomNumber).toLowerCase()}`);
  }
  mapData.bathroomRooms = bathroomRooms;

  const out = [];
  for (const place of places || []) {
    if (!wanted.has(placeKindKey(place))) continue;
    const loc = resolvePlace(mapData, place);
    if (!loc || !loc.position) continue;
    out.push({
      place,
      loc,
      buildingName: nameBySlug.get(place.building) || prettifySlug(place.building),
    });
  }
  mapData.places = out;
  return out;
}

function prettifySlug(slug) {
  return String(slug || '').split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/**
 * Places sorted by straight-line distance from `origin`, each gaining a
 * `meters` field. With no origin the list falls back to alphabetical, so the
 * panel is still useful before geolocation resolves (or when it is refused).
 */
/** The neutral state of the attribute filters. */
export const NO_FILTERS = {
  // study
  capacity: null, plugs: false, zone: null, tables: null,
  // bathroom
  gender: null, accessible: false, period: false,
};

export const hasActiveFilters = (f) =>
  !!(f && (f.capacity || f.plugs || f.zone || f.tables || f.gender || f.accessible || f.period));

/**
 * Does a place pass the attribute filters?
 *
 * The zone rule is the interesting one: **52 of 65 study spots are marked
 * neither quiet nor loud**, so treating "unclassified" as a miss would empty
 * the list the moment anyone touched the filter. Unclassified therefore
 * qualifies as *either*, and only an explicit opposite marking excludes a
 * place. (Same principle as Welcome Day counting a universal washroom under
 * both men's and women's.)
 */
export function matchesFilters(place, f) {
  if (!f) return true;
  if (place.type === 'bathroom') {
    // A universal washroom answers both a men's and a women's search — the same
    // rule Welcome Day's bathroom finder uses.
    if (f.gender === 'mens' && !['mens', 'universal'].includes(place.bathroomType)) return false;
    if (f.gender === 'womens' && !['womens', 'universal'].includes(place.bathroomType)) return false;
    if (f.accessible && !place.accessible) return false;
    if (f.period && !place.periodSupply) return false;
    return true;
  }
  // Only study spots answer the capacity/plugs/zone/tables questions. The
  // filter row is already hidden for the other kinds, but the chosen filters
  // persist across a tab change — and a library carries no capacity and no
  // amenities, so letting them fall through to the rules below emptied the
  // Libraries tab whenever a study filter happened to be left on.
  if (place.type !== 'study') return true;
  const am = place.amenities || {};
  if (f.capacity && place.capacity !== f.capacity) return false;
  if (f.plugs && !am.plugs) return false;
  if (f.zone === 'quiet' && am.loud && !am.quiet) return false;
  if (f.zone === 'loud' && am.quiet && !am.loud) return false;
  if (f.tables === 'chairs' && !am.noTables) return false;
  if (f.tables === 'tables' && am.noTables) return false;
  return true;
}

export function nearbyPlaces(entries, origin, { kind = null, query = '', filters = null } = {}) {
  const t = String(query).trim().toLowerCase();
  let list = entries || [];
  if (kind) list = list.filter(e => placeKindKey(e.place) === kind);
  if (filters) list = list.filter(e => matchesFilters(e.place, filters));
  if (t) {
    list = list.filter(e =>
      placeTitle(e.place).toLowerCase().includes(t)
      || String(e.buildingName || '').toLowerCase().includes(t)
      || String(e.place.roomNumber || '').toLowerCase().includes(t)
      // The note is where the lounge/café/foodcourt a thing sits in is named,
      // and that is often the only word someone knows it by.
      || placeNote(e.place).toLowerCase().includes(t));
  }
  const scored = list.map(e => ({
    ...e,
    meters: origin ? haversineMeters(origin, e.loc.position) : null,
  }));
  scored.sort((a, b) => (
    a.meters != null && b.meters != null
      ? a.meters - b.meters
      : placeTitle(a.place).localeCompare(placeTitle(b.place))
  ));
  return scored;
}

/** "120 m" / "1.4 km" — same rounding the rest of the app uses for walk distance. */
export function formatMeters(meters) {
  if (meters == null || !Number.isFinite(meters)) return '';
  return meters < 1000 ? `${Math.round(meters)} m` : `${(meters / 1000).toFixed(1)} km`;
}

/** Short floor tag for a badge: 1, 2, T (tunnel), O (outdoors). */
export const floorTag = (f) => (f === -1 ? 'T' : f === 0 ? 'O' : String(f));
